import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { toLibraryEntry } from '@/api/mappers';
import { LIBRARY_LABELS } from '@/lib/constants';
import * as libraryStore from '@/lib/libraryStore';
import { useToast } from '@/context/ToastProvider';
import type { AnimeSummary, LibraryEntry, LibraryKey } from '@/types/anime';

/**
 * Cầu nối React ↔ libraryStore. Mọi component dùng hook này đều re-render khi
 * thư viện đổi, kể cả khi thay đổi đến từ một tab khác.
 */
export function useLibrary() {
	const { notify } = useToast();
	const state = useSyncExternalStore(
		libraryStore.subscribe,
		libraryStore.getSnapshot,
		libraryStore.getSnapshot,
	);

	const counts = useMemo(
		() => ({
			favorites: state.favorites.length,
			watchlist: state.watchlist.length,
			history: state.history.length,
		}),
		[state],
	);

	const isSaved = useCallback(
		(key: LibraryKey, slug: string) =>
			state[key].some((entry) => entry.slug === slug),
		[state],
	);

	const toggle = useCallback(
		(key: LibraryKey, anime: AnimeSummary) => {
			const added = libraryStore.toggleEntry(key, toLibraryEntry(anime));
			const storeError = libraryStore.getLastError();
			if (storeError) {
				notify(storeError, 'error');
				return false;
			}
			notify(
				added
					? `Đã thêm vào ${LIBRARY_LABELS[key]}`
					: `Đã bỏ khỏi ${LIBRARY_LABELS[key]}`,
				added ? 'success' : 'info',
			);
			return added;
		},
		[notify],
	);

	const remove = useCallback(
		(key: LibraryKey, slug: string) => {
			libraryStore.removeEntry(key, slug);
			notify(`Đã bỏ khỏi ${LIBRARY_LABELS[key]}`, 'info');
		},
		[notify],
	);

	const clear = useCallback(
		(key: LibraryKey) => {
			libraryStore.clearLibrary(key);
			notify(`Đã xoá ${LIBRARY_LABELS[key]}`, 'info');
		},
		[notify],
	);

	/** Ghi lịch sử — im lặng, không toast, vì được gọi tự động khi xem. */
	const recordHistory = useCallback(
		(anime: AnimeSummary, extra?: Partial<LibraryEntry>) => {
			libraryStore.recordHistory(toLibraryEntry(anime, extra));
		},
		[],
	);

	/** Bản ghi mới nhất, dùng cho thẻ "Đang xem tiếp" ở sidebar. */
	const continueWatching = state.history[0] ?? null;

	return {
		favorites: state.favorites,
		watchlist: state.watchlist,
		history: state.history,
		counts,
		continueWatching,
		isSaved,
		toggle,
		remove,
		clear,
		recordHistory,
	};
}
